import React from "react";

import { GetMenuItemsAvailableFilters } from "@/api/requests/getMenuItems";

import { Facet, FilteredGrid } from "@/components/FilteredGrid";
import { FilterChips } from "@/components/FilteredGrid/FilterChips";
import DesktopFilterPlaceholder from "@/components/MenuFilters/DesktopFilterPlaceholder";

import { MenuPage } from "../types/MenuFilters";
import {
  useAPISupportedFilterFacets,
  useFilterQueryParams,
  useNonAPISupportedFilterFacets,
} from "./hooks";
import MenuFilterGroups from "./MenuFilterGroups";
import MobileMenuFilterShortcuts from "./MobileMenuFilterShortcuts";

const MenuFilters: React.FC<{
  allowNonAPISupportedFacets?: boolean;
  availableFilters: GetMenuItemsAvailableFilters;
  children: React.ReactNode;
  renderHeader?: () => React.ReactNode;
  menuPage?: MenuPage;
  loading?: boolean;
  mobileFilterPanelImpression?: { [key: string]: string | boolean };
  onClearAllClick?: (facet?: Facet) => void;
  mobileClassName?: string;
  fulfillmentModeUnavailable?: boolean;
}> = ({
  allowNonAPISupportedFacets,
  availableFilters,
  children,
  renderHeader,
  menuPage = MenuPage.dispensary,
  loading,
  onClearAllClick,
  mobileFilterPanelImpression,
  mobileClassName,
  fulfillmentModeUnavailable,
}) => {
  const menuFilterMap = availableFilters.reduce(
    (filterMap, availableFilter) => ({
      ...filterMap,
      [availableFilter.name]: availableFilter,
    }),
    {},
  );

  const apiSupportedFacets = useAPISupportedFilterFacets(menuFilterMap);
  const nonAPISupportedFacets = useNonAPISupportedFilterFacets(menuFilterMap);
  const {
    actions: { toggleFilter },
  } = useFilterQueryParams();

  return (
    <div data-testid="menu-filter-container">
      <FilteredGrid
        confirmationButtonText="confirm"
        apiSupportedFacets={
          allowNonAPISupportedFacets
            ? [...apiSupportedFacets, ...nonAPISupportedFacets]
            : apiSupportedFacets
        }
        renderHeader={renderHeader}
        menuPage={menuPage}
        onClearAllClick={onClearAllClick}
        mobileClassName={mobileClassName}
        fulfillmentModeUnavailable={fulfillmentModeUnavailable}
        renderFilters={(_facets, defaultOpenKey) =>
          availableFilters.length > 0 ? (
            <MenuFilterGroups
              availableFilters={availableFilters}
              menuFilterMap={menuFilterMap}
              defaultOpenKey={defaultOpenKey}
              menuPage={menuPage}
              loading={loading}
            />
          ) : (
            <DesktopFilterPlaceholder />
          )
        }
        renderChips={(facets: Facet[]) => (
          <FilterChips
            facets={facets}
            onClick={(facet: Facet) =>
              onClearAllClick
                ? onClearAllClick(facet)
                : toggleFilter(facet.key, facet.value)
            }
          />
        )}
        availableFilters={availableFilters}
        mobileFilterPanelImpression={mobileFilterPanelImpression}
        mobileShortcuts={MobileMenuFilterShortcuts}
      >
        {children}
      </FilteredGrid>
    </div>
  );
};

export default MenuFilters;
